import { type ReactNode, useEffect, useRef, useState } from "react";
import { isFrameLikeElement } from "@excalidraw/element";
import { getCommonBoundingBox, getElementAbsoluteCoords } from "@excalidraw/element";
import { mutateElement } from "@excalidraw/element";
import { sceneCoordsToViewportCoords } from "..";
import { atom } from "../editor-jotai";
import { CLASSES } from "@excalidraw/common";
import { trackEvent } from "../analytics";
import { pointFrom, pointRotateRads, type LocalPoint } from "@excalidraw/math";
import { RectangleIcon } from "./icons";
import type { ExcalidrawFrameLikeElement } from "@excalidraw/element/types";
import type { AppClassProperties } from "../types";



// Common screen sizes for UI design
const FRAME_SIZES = [
  {
    name: "Mobile Portrait",
    width: 375,
    height: 667,
    label: "Mobile",
  },
  {
    name: "Mobile Landscape", 
    width: 667,
    height: 375,
    label: "Mobile L",
  },
  {
    name: "Tablet Portrait",
    width: 768,
    height: 1024,
    label: "Tablet",
  },
  {
    name: "Tablet Landscape",
    width: 1024,
    height: 768,
    label: "Tablet L",
  },
  {
    name: "Desktop Small",
    width: 1280,
    height: 720,
    label: "Desktop",
  },
  {
    name: "Desktop Large",
    width: 1440,
    height: 900,
    label: "Desktop L",
  },
  {
    name: "Full HD",
    width: 1920,
    height: 1080,
    label: "Full HD",
  },
] as const;

export type FrameResizePopupState = {
  type: "panel";
} | null;

export const frameResizePopupAtom = atom<FrameResizePopupState>(null);

const GAP_HORIZONTAL = 8;
const GAP_VERTICAL = 10;

const FrameResizePopup = ({ app }: { app: AppClassProperties }) => {
  const selectedElements = app.scene.getSelectedElements(app.state);
  const selectedFrames = selectedElements.filter(
    (element): element is ExcalidrawFrameLikeElement =>
      isFrameLikeElement(element),
  );

  // Close popup if no frames are selected
  useEffect(() => {
    if (selectedFrames.length === 0) {
      app.updateEditorAtom(frameResizePopupAtom, null);
      return;
    }
  }, [selectedFrames, app]);

  return <Panel app={app} frames={selectedFrames} />;
};

const Panel = ({
  app,
  frames,
}: {
  app: AppClassProperties;
  frames: ExcalidrawFrameLikeElement[];
}) => {
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef("");
  const panelRef = useRef<HTMLDivElement>(null);

  // Position popup above the selected frames
  useEffect(() => {
    if (frames.length === 0) return;
    const elements = [...frames].sort((a, b) =>
      a.id.localeCompare(b.id),
    );
    const newPositionRef = `
      ${app.state.scrollX}${app.state.scrollY}${app.state.offsetTop}${
      app.state.offsetLeft
    }${app.state.zoom.value}${elements.map((el) => el.id).join(",")}`;

    if (newPositionRef === positionRef.current) {
      return;
    }

    positionRef.current = newPositionRef;

    let topLeft;

    if (elements.length === 1) {
      const [x1, y1, , , cx, cy] = getElementAbsoluteCoords(
        elements[0],
        app.scene.getNonDeletedElementsMap(),
      );
      topLeft = pointRotateRads(
        pointFrom(x1, y1),
        pointFrom(cx, cy),
        elements[0].angle,
      );
    } else {
      const { minX, minY } = getCommonBoundingBox(elements);
      topLeft = pointFrom(minX, minY);
    }

    const { x, y } = sceneCoordsToViewportCoords(
      { sceneX: topLeft[0], sceneY: topLeft[1] },
      app.state,
    );

    setPanelPosition({ x, y });
  }, [frames, app.scene, app.state]);



  const resizeFrames = (targetSize: { width: number; height: number }) => {
    const elementsMap = app.scene.getNonDeletedElementsMap();
    
    for (const frame of frames) {
      mutateElement(frame, elementsMap, {
        width: targetSize.width,
        height: targetSize.height,
      });
    }

    app.scene.triggerUpdate();
    trackEvent("frameResize", "preset", "ui");
  };

  // Don't render if no frames
  if (frames.length === 0) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      style={{
        position: "absolute",
        top: `${
          panelPosition.y -
          (GAP_VERTICAL + 112) * app.state.zoom.value -
          app.state.offsetTop
        }px`,
        left: `${panelPosition.x - app.state.offsetLeft - GAP_HORIZONTAL}px`,
        zIndex: 2,
      }}
      className={CLASSES.CONVERT_ELEMENT_TYPE_POPUP}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          app.updateEditorAtom(frameResizePopupAtom, null);
        } else if (event.key === "Tab") {
          event.preventDefault();
          const currentIndex = 0; // Could implement cycling through options
          const nextIndex = event.shiftKey
            ? (FRAME_SIZES.length + currentIndex - 1) % FRAME_SIZES.length
            : (currentIndex + 1) % FRAME_SIZES.length;
          
          const targetSize = FRAME_SIZES[nextIndex];
          resizeFrames({ width: targetSize.width, height: targetSize.height });
        }
      }}
    >
      {FRAME_SIZES.map((size) => (
        <button
          className="Shape"
          key={`${frames[0].id}${frames[0].version}_${size.name}`}
          type="button"
          title={`${size.name} (${size.width}×${size.height})`}
          aria-label={`Resize to ${size.name}`}
          data-testid={`frame-resize-${size.name.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => {
            trackEvent("canvas", "frameResize", size.name);
            resizeFrames({ width: size.width, height: size.height });
            panelRef.current?.focus();
          }}
        >
          {size.label}
        </button>
      ))}
    </div>
  );
};

export { FrameResizePopup }; 